import React from 'react';
import ExecutiveReasonCell from './shared/ExecutiveReasonCell';

const Section10 = ({
  mode,
  collapsedSections,
  renderSectionHeader,
  dsoData,
  allReasonsData,
  isLoadingReasons,
  handleOpenReasonModal,
  canAddReason,
  onSubmitReasonInline,
  isSubmittingReason,
  sectionNote
}) => {
  // Filter reasons for Priority Escalation topic
  const priorityEscalationReasons = Array.isArray(allReasonsData)
    ? allReasonsData.filter((reason) => reason.topic === 'Priority Escalation')
    : [];

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      {renderSectionHeader(9, 'Executive Performance Metrics & Escalations')}
      {(mode === 'view' || !collapsedSections[10]) && (
        <>
          <div className="overflow-x-auto">
            {dsoData?.data?.sales?.length === 0 ? (
              <div className="flex justify-center items-center h-[8rem]">
                <p className="text-sm text-gray-500">No data available</p>
              </div>
            ) : (
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Executive
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Total Follow Ups
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Timely Follow Ups
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Escalation Follow Ups
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                      Performance Score
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase w-[25rem]">
                      Comment
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {dsoData?.data?.sales?.map((row, index) => (
                    <tr key={index}>
                      <td className="px-4 py-3 font-medium text-gray-900">{row.name}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {Number(row.closedFollowUp || 0) + Number(row.activeFollowUp || 0) || 0}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.closedFollowUp || 0}</td>
                      <td className="px-4 py-3 text-sm text-gray-900">{row.activeFollowUp || 0}</td>
                      <td className="px-4 py-3">
                        <div className="flex-1 max-w-[200px] bg-gray-200 rounded-full h-6 relative overflow-hidden">
                          <div
                            className={`h-6 rounded-full transition-all duration-500 ${
                              (row.performanceScore || 0) >= 80
                                ? 'bg-green-500'
                                : (row.performanceScore || 0) >= 60
                                  ? 'bg-blue-600'
                                  : (row.performanceScore || 0) >= 40
                                    ? 'bg-yellow-500'
                                    : 'bg-red-500'
                            }`}
                            style={{
                              width: `${Math.min(Math.max(row.performanceScore || 0, 0), 100)}%`,
                              minWidth: (row.performanceScore || 0) > 0 ? '4px' : '0'
                            }}
                          />
                          {/* Percentage always centered in the middle of the entire bar */}
                          <span
                            className={`absolute inset-0 flex items-center justify-center text-xs font-semibold whitespace-nowrap ${
                              (row.performanceScore || 0) === 0
                                ? 'text-gray-500'
                                : (row.performanceScore || 0) >= 80
                                  ? 'text-green-700'
                                  : (row.performanceScore || 0) >= 60
                                    ? 'text-blue-700'
                                    : (row.performanceScore || 0) >= 40
                                      ? 'text-yellow-700'
                                      : 'text-red-700'
                            }`}>
                            {Math.round(row.performanceScore || 0)}%
                          </span>
                        </div>
                      </td>
                      <ExecutiveReasonCell
                        row={row}
                        mode={mode}
                        handleOpenReasonModal={handleOpenReasonModal}
                        priorityEscalationReasonsData={{ data: priorityEscalationReasons }}
                        isLoadingReasons={isLoadingReasons}
                        canAddReason={canAddReason}
                        onSubmitReasonInline={onSubmitReasonInline}
                        isSubmittingReason={isSubmittingReason}
                      />
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
      {/* Section Note */}
      {sectionNote && (
        <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <svg
              className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
              />
            </svg>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="text-sm font-semibold text-gray-900">
                  {sectionNote.user?.vertical || 'Management'} notes
                </h4>
                <div className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-100 rounded-full">
                  <span className="text-xs text-gray-600">By</span>
                  <span className="text-xs font-medium text-gray-900">
                    {sectionNote.user?.name || 'Unknown User'}
                  </span>
                </div>
              </div>
              <p className="text-sm text-gray-700 leading-relaxed">{sectionNote.note}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Section10;
